import React from 'react';
import { useHistory } from 'react-router';

// Types
import { ReviewTemplateType, ReviewTemplateTypeType } from 'Shared/types/ReviewTemplates.type';

// Utils
import PageUrlsUtil from 'Shared/utils/PageUrls.utils';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useUser } from 'Shared/utils/hooks/use-user';
import { useReviewTemplate } from 'Shared/utils/hooks/use-review-template';

// Components
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpBadge } from 'Wpds/components/badge';
import { WpButton } from 'Wpds/components/button';
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import WpInlineError from 'Wpds/components/inline-error/WpInlineError';
import WpIcon from 'Wpds/components/icon/WpIcon';
import DeleteReviewTemplateModal from 'Shared/components/DeleteReviewTemplateModal/DeleteReviewTemplateModal';
import ReviewTemplatePreviewModal from 'Dashboard/components/ReviewTemplatePreviewPage/ReviewTemplatePreviewModal';

// Styles
import './ReviewTemplateListItem.scss';

type Props = {
  template: ReviewTemplateType;
  handleChooseTemplate: Function;
};

const ReviewTemplateListItem = ({ template, handleChooseTemplate }: Props) => {
  const history = useHistory();
  const { user } = useUser({ id: template.userId });
  const {
    isDuplicating,
    handleDuplicateReviewTemplate,
    isDeleting,
    handleDeleteReviewTemplate,
    hasErrors,
  } = useReviewTemplate({
    slug: template.slug,
    withFetch: false,
  });
  const { id: currentUserId } = useCurrentUser();
  const [isDeleteModalOpen, toggleIsDeleteModalOpen] = useToggle(false);
  const [isPreviewModalOpen, toggleIsPreviewModalOpen] = useToggle(false);

  const name = template.name || 'Untitled';
  const isCurrentUserCreator = template.isCustom && currentUserId === template.userId;

  const handleOnEditClick = () => {
    if (template.isUsed) {
      alert(
        'You cannot edit this template because it was used in a 360 Feedback. Instead please duplicate the template and edit the duplicate.'
      );
      return;
    }
    template.templateType === ReviewTemplateTypeType.performanceReview
      ? history.push(PageUrlsUtil.makePerformanceReviewTemplateUrl({ slug: template.slug }))
      : history.push(PageUrlsUtil.makeCoachingFeedbackTemplateUrl({ slug: template.slug }));
  };

  const handleGiveFeedbackClick = () => {
    if (hasErrors && isCurrentUserCreator) {
      handleOnEditClick();
    }
    if (hasErrors) return;
    if (handleChooseTemplate) handleChooseTemplate({ template });
  };

  const handleDuplicateClick = () => {
    handleDuplicateReviewTemplate().then(response => {
      history.push(PageUrlsUtil.makeCoachingFeedbackTemplateUrl({ slug: response.data.attributes.slug }));
    });
  };

  const baseClassName = 'ReviewTemplateListItem';

  const templateExistsInDatabase = template.createdAt !== '';

  return (
    <>
      <WpContainer
        className={baseClassName}
        onClick={handleGiveFeedbackClick}
        qaTest={`ReviewTemplateListItem-${template.slug}`}
      >
        <WpContainerBody className={`${baseClassName}__body`}>
          <div className={`${baseClassName}__body__header`}>
            <h5>{name}</h5>
            {templateExistsInDatabase && (
              <div onClick={e => e.stopPropagation()}>
                <WpDropdown placement="bottom-end">
                  <WpDropdownToggle>
                    <WpButton size="sm" variant="tertiary" color="primary" loading={isDuplicating || isDeleting}>
                      <WpIcon name="ellipsis-h-alt" />
                    </WpButton>
                  </WpDropdownToggle>
                  <WpDropdownMenu>
                    <WpDropdownMenuItem onClick={handleDuplicateClick}>
                      <WpIcon name="copy" />
                      Duplicate Template
                    </WpDropdownMenuItem>
                    {!hasErrors && (
                      <WpDropdownMenuItem onClick={toggleIsPreviewModalOpen}>
                        <WpIcon name="eye" />
                        Preview Template
                      </WpDropdownMenuItem>
                    )}
                    {isCurrentUserCreator && (
                      <>
                        <WpDropdownMenuItem onClick={handleOnEditClick}>
                          <WpIcon name="pencil" />
                          Edit Template
                        </WpDropdownMenuItem>
                        <WpDropdownMenuDivider />
                        <WpDropdownMenuItem onClick={toggleIsDeleteModalOpen} color="danger">
                          <WpIcon name="trash" />
                          Delete Template
                        </WpDropdownMenuItem>
                      </>
                    )}
                  </WpDropdownMenu>
                </WpDropdown>
              </div>
            )}
          </div>
          <div>
            {template.isCustom && user ? (
              <WpBadge color="blue" size="sm">
                Created by: {user.displayName}
              </WpBadge>
            ) : (
              <WpBadge color="gray" size="sm">
                Default
              </WpBadge>
            )}
          </div>
          {template.isCustom && template.isUsed && (
            <div>
              <WpBadge color="gray" size="sm">
                In use
              </WpBadge>
            </div>
          )}
          <p className={`${baseClassName}__body__description`}>{template.description}</p>
          <div onClick={e => e.stopPropagation()} className={`${baseClassName}__body__button-wrapper`}>
            {handleChooseTemplate && (
              <WpButton variant="secondary" onClick={handleGiveFeedbackClick} disabled={hasErrors}>
                Give Feedback
              </WpButton>
            )}
            {hasErrors && !template.isUsed && (
              <div className={`${baseClassName}__body__error`}>
                <span className={`${baseClassName}__body__error__icon`}>
                  <WpIcon name="exclamation-circle" size="lg" />
                </span>
                <WpInlineError error="Incomplete questions" />
              </div>
            )}
          </div>
        </WpContainerBody>
      </WpContainer>
      <DeleteReviewTemplateModal
        isOpen={isDeleteModalOpen}
        onToggle={toggleIsDeleteModalOpen}
        handleDelete={handleDeleteReviewTemplate}
      />
      <ReviewTemplatePreviewModal
        reviewTemplateSlug={template.slug}
        isOpen={isPreviewModalOpen}
        toggleIsOpen={toggleIsPreviewModalOpen}
        isPerformaneReviewTemplate={template.templateType === ReviewTemplateTypeType.performanceReview}
      />
    </>
  );
};

export default ReviewTemplateListItem;
